import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  Divider,
  Box,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Badge,
  CalendarToday,
} from '@mui/icons-material';
import AvatarEditor from '../../components/AvatarEditor';
import MembershipActivationButton from '../../components/MembershipActivationButton';
import apiClient from '../../config/axios';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getMembershipStatusColor, 
  getMembershipStatusText, 
  getRoleDisplayName, 
  getRoleColor,
  formatCurrency
} from './utils';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { useNavigate } from 'react-router-dom';
import getAppConfig from '../../config/app-config';

const ProfileSidebar = ({ user, membership }) => {
  const navigate = useNavigate();
  const { updateUser, isProd, isDev } = useAuth();
  const config = getAppConfig();
  const isRockdreamsProduction = config.ASSOCIATE === 'rockdreams' && (isProd || isDev);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarError, setAvatarError] = useState(null);
  const [selectedAvatar, setSelectedAvatar] = useState(user.avatar || '');
  const [formData, setFormData] = useState({
    avatar: user.avatar || ''
  });

  const numAvatars = 9;
  const availableAvatars = Array.from({ length: numAvatars }, (_, i) => `default${i + 1}.jpg`);

  const handleAvatarSave = async () => {
    setAvatarLoading(true);
    setAvatarError(null);
    try {
      const response = await apiClient.patch('/api/user/update-user', { avatar: selectedAvatar });
      updateUser({ ...user, ...response.data });
      setFormData(prev => ({ ...prev, avatar: selectedAvatar }));
      setAvatarModalOpen(false);
      // const response = await apiClient.patch('/api/user/update-user', { avatar: selectedAvatar });
      // // Update the user context if needed
      // setFormData(prev => ({ ...prev, avatar: selectedAvatar }));
      // setAvatarModalOpen(false);
    } catch (error) {
      setAvatarError(error.response?.data?.message || 'Errore durante il salvataggio dell\'immagine');
    } finally {
      setAvatarLoading(false);
    }
  };

  return (
    <Grid container spacing={2}>
      {/* User Avatar and Roles */}
      <Grid size={12}>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Avatar
              src={formData.avatar ? (formData.avatar.startsWith('data:image/') ? formData.avatar : `/avatars/${formData.avatar}`) : ''}
              sx={{ width: 100, height: 100, mx: 'auto', mb: 2, bgcolor: 'primary.main', fontSize: '2rem' }}
            >
              {!formData.avatar && '?'}
            </Avatar>
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                sx={{ minHeight: 24, minWidth: 24, px: 1, py: 0, fontSize: '0.75rem', lineHeight: 1 }}
                onClick={() => setAvatarModalOpen(true)}
              >
                Cambia immagine
              </Button>
            </Box>
            <Dialog open={avatarModalOpen} onClose={() => setAvatarModalOpen(false)}>
              <DialogTitle>Modifica immagine profilo</DialogTitle>
              <DialogContent>
                <AvatarEditor
                  value={formData.avatar || ''}
                  availableAvatars={availableAvatars}
                  onSelectedAvatarChange={setSelectedAvatar}
                />
                {avatarLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
                {avatarError && (
                  <Alert severity="error" sx={{ mt: 2 }}>{avatarError}</Alert>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setAvatarModalOpen(false)}
                  disabled={avatarLoading}
                >
                  Annulla
                </Button>
                <Button
                  onClick={handleAvatarSave}
                  disabled={avatarLoading || !selectedAvatar || selectedAvatar === (formData.avatar || '')}
                >
                  Salva
                </Button>
              </DialogActions>
            </Dialog>
            <Typography variant="h6" gutterBottom>
              {user.name} {user.surname}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
              {user.roles?.map((role) => (
                <Chip
                  key={role}
                  label={getRoleDisplayName(role)}
                  color={getRoleColor(role)}
                  size="small"
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Membership Status */}
      {!isRockdreamsProduction && (
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <Badge sx={{ mr: 1 }} />
                Tesseramento
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {membership ? (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Status:
                    </Typography>
                    <Chip
                      label={getMembershipStatusText(membership.status)}
                      color={getMembershipStatusColor(membership.status)}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Tessera:
                    </Typography>
                    <Typography variant="body2">
                      {membership.membershipId}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Scadenza:
                    </Typography>
                    <Typography variant="body2">
                      {membership.endDate ? dayjs(membership.endDate).locale('it').format('DD-MMM-YY') : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Importo:
                    </Typography>
                    <Typography variant="body2">
                      {formatCurrency(membership.amount)}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Nessun tesseramento attivo.
                  </Typography>
                  <MembershipActivationButton />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Quick Actions */}
      {!isRockdreamsProduction && (
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Azioni Rapide
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CalendarToday />}
                  onClick={() => navigate('/courses')}
                >
                  Iscriviti ai Corsi
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Badge />}
                  onClick={() => navigate('/my-courses')}
                >
                  Le mie iscrizioni
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CalendarToday />}
                  onClick={() => navigate('/member-calendar')}
                >
                  Mio calendario
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

export default ProfileSidebar; 